import { useEffect, useRef } from 'react'
import type { TourActionId } from '@/lib/productTours/anchors'

/**
 * Actions de tour déclarées par l'écran affiché : `open` avant l'étape, `close` en la quittant
 * (remettre l'écran comme le tour l'a trouvé). Un seul écran à la fois les déclare : une table de
 * module suffit, sans contexte React entre la page et le moteur.
 */
export type TourActionHandler = {
  open: () => void
  close?: () => void
}

const handlers = new Map<string, TourActionHandler>()

export const getTourAction = (id: string): TourActionHandler | undefined =>
  handlers.get(id)

export const isTourActionAvailable = (id: string): boolean => handlers.has(id)

/**
 * Déclare les actions de l'écran pendant qu'il est monté. Les fonctions sont relues à chaque appel :
 * pas besoin de les mémoïser côté écran.
 */
export const useTourActions = (
  actions: Partial<Record<TourActionId, TourActionHandler>>
): void => {
  const latest = useRef(actions)
  latest.current = actions
  const ids = Object.keys(actions).sort().join(',')

  useEffect(() => {
    const registered = ids ? (ids.split(',') as TourActionId[]) : []
    registered.forEach((id) =>
      handlers.set(id, {
        open: () => latest.current[id]?.open(),
        close: () => latest.current[id]?.close?.(),
      })
    )
    return () => registered.forEach((id) => handlers.delete(id))
  }, [ids])
}
